import { NavLink } from 'react-router';
import { useApprovals } from '@/api/hooks';
import type { Me } from '@/api/session';
import { Icon } from '@/ui/icons';
import { Count } from '@/ui/primitives';
import { useFrame } from './frame';
import { doorsOf, tabsFor, type Tab } from './nav';
import { useDepartmentNames } from './departments';
import { useRails } from './NavRail';
import { useScope } from './scope';

/**
 * The phone's own navigation (gallery TabBar, G:439-466): 52px, at most four
 * items, the glyph over its word, the current one filled. Which four is the
 * scope and the person's role (`tabsFor`, FRONTEND-AUDIT B3); everything else is one press
 * away in the menu, which opens the rail as a drawer.
 */
export function TabBar({ me }: { me: Me }) {
  const frame = useFrame();
  const { scope } = useScope(me);
  const rail = useRails(me, useDepartmentNames())(scope);
  // A tab is a door of the rail's: Finance, read-only, has no Approvals.
  const { tabs, menu } = tabsFor(me, scope, doorsOf(rail).some((d) => d.to === '/approvals'));
  return (
    <nav aria-label="Tabs" className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-page pb-[env(safe-area-inset-bottom)]">
      {tabs.map((t) => (t.count === 'approvals' ? <ApprovalsTab key={t.to} tab={t} me={me} /> : <TabLink key={t.to} tab={t} />))}
      {menu && (
        <button type="button" className={`${CELL} text-secondary hover:text-ink`} onClick={() => frame.setDrawerOpen(true)} aria-expanded={frame.drawerOpen}>
          <Icon glyph="menu" />
          Menu
        </button>
      )}
    </nav>
  );
}

const CELL = 'relative flex min-h-[52px] flex-1 flex-col items-center justify-center gap-1 text-micro uppercase tracking-label no-underline';

function TabLink({ tab, n = 0 }: { tab: Tab; n?: number }) {
  return (
    <NavLink
      to={tab.to}
      end={tab.to === '/today'}
      aria-label={n > 0 ? `${tab.label}, ${n} waiting` : tab.label}
      className={({ isActive }) => `${CELL} ${isActive ? 'bg-ink text-page hover:text-page' : 'text-secondary hover:text-ink'}`}
    >
      <Icon glyph={tab.glyph} />
      {tab.label}
      {n > 0 && (
        <span className="absolute right-[calc(50%-22px)] top-1.5">
          <Count n={n} />
        </span>
      )}
    </NavLink>
  );
}

function ApprovalsTab({ tab, me }: { tab: Tab; me: Me }) {
  const approvals = useApprovals(useScope(me).scope.kind === 'department');
  return <TabLink tab={tab} n={approvals.data?.items.length ?? 0} />;
}
